import * as amplitude from "@amplitude/analytics-browser";
import { useRequiredContext } from "@redotech/react-util/context";
import { FilterModal } from "@redotech/redo-merchant-app/support/filter-modal";
import { FilterOptions, FiltersStatus, View } from "@redotech/redo-model/view";
import {
  Button,
  ButtonSize,
  ButtonTheme,
  IconButton,
} from "@redotech/redo-web/button";
import { Flex } from "@redotech/redo-web/flex";
import FilterIcon from "@redotech/redo-web/icon-old/filter.svg";
import SearchIcon from "@redotech/redo-web/icon-old/search.svg";
import XIcon from "@redotech/redo-web/icon-old/x.svg";
import { TextInput } from "@redotech/redo-web/text-input";
import { memo, useContext, useState } from "react";
import { useNavigate } from "react-router-dom";
import { TeamContext } from "../../app/team";
import { ReloadViewsContext } from "../../app/views";
import { RedoMerchantClientContext } from "../../client/context";
import { filtersToQueryParams } from "../../client/conversations";
import { createView } from "../../client/view";
import { FiltersContext, SetFiltersContext } from "../support";

export const ConversationTableFiltersHeader = memo(
  function ConversationTableFiltersHeader({
    view,
    initialFilters,
    statusFilter,
    searchString,
    setSearchString,
  }: {
    searchString: string | undefined;
    setSearchString: (searchString: string | undefined) => void;
    view?: View;
    initialFilters: FilterOptions;
    statusFilter: FiltersStatus | "all";
  }) {
    const navigate = useNavigate();

    const filters = useRequiredContext(FiltersContext);
    const setFilters = useRequiredContext(SetFiltersContext);
    const team = useContext(TeamContext);
    const client = useRequiredContext(RedoMerchantClientContext);
    const reloadViews = useContext(ReloadViewsContext);

    const handleSearchAll = () => {
      amplitude.logEvent("search-conversation", { mode: "all", searchString });
      navigate({
        pathname: "../table/all",
        search: `?initialSearch=${encodeURIComponent(searchString || "")}`,
      });
    };

    const handleCreateView = async (
      filters: FilterOptions,
      name: string,
      isPrivate: boolean,
    ) => {
      if (team) {
        amplitude.logEvent("create-view", { view: name });
        const view = await createView(client, { filters, name, isPrivate });
        if (reloadViews) {
          reloadViews();
        }
        window.location.href = `/stores/${team._id}/support/table/${view.name}`;
      }
    };

    const applyFilters = (filters: FilterOptions) => {
      amplitude.logEvent("filter-conversation", { filters });
      const filterQueryParams = filtersToQueryParams(filters);
      const queryParams = new URLSearchParams(
        filters.modified
          ? filterQueryParams
            ? `?modified=true&${filterQueryParams}`
            : `?modified=true`
          : "",
      );
      const existingQueryParams = new URLSearchParams(location.search);
      const searchParam = existingQueryParams.get("search");
      if (searchParam) {
        queryParams.set("search", searchParam);
      }

      navigate({
        pathname: location.pathname,
        search: queryParams.toString(),
      });
      setFilters({
        ...filters,
        status:
          statusFilter === "all"
            ? Object.values(FiltersStatus)
            : [statusFilter],
      });
    };

    const [filterModalOpen, setFilterModalOpen] = useState(false);

    return (
      <>
        <FilterModal
          applyFilters={applyFilters}
          createView={handleCreateView}
          current={
            Object.keys(filters).length > 0
              ? filters
              : view?.filters || initialFilters
          }
          defaults={initialFilters}
          open={filterModalOpen}
          setOpen={setFilterModalOpen}
        />
        <Flex dir="column" gap="lg">
          <Flex gap="3xl">
            <TextInput
              button={
                statusFilter === "all"
                  ? undefined
                  : !!searchString && (
                      <Button
                        onClick={handleSearchAll}
                        size={ButtonSize.MICRO}
                        theme={ButtonTheme.SOLID_LIGHT}
                      >
                        Search all
                      </Button>
                    )
              }
              icon={
                searchString ? (
                  <XIcon onClick={() => setSearchString("")} />
                ) : (
                  <SearchIcon />
                )
              }
              onChange={setSearchString}
              placeholder="Search"
              value={searchString || ""}
            />
            <IconButton
              onClick={() => {
                amplitude.logEvent("view-filterModal");
                setFilterModalOpen(true);
              }}
              size={ButtonSize.SMALL}
            >
              <FilterIcon />
            </IconButton>
          </Flex>
        </Flex>
      </>
    );
  },
);
